import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import queryString from 'query-string'
import { CardFeedCardStatusFilter } from 'src/queries/cardFeeds/cardFeeds.models'
import { cardFeedSavedViewsQueryKeys } from 'src/queries/queries.constants'
import { useExtendDelete, useRequestConfig } from 'src/queries/queries.utils'
import { BackendErrorPayload } from 'src/utils/error.utils'

export interface CardFeedSavedViewFilters {
  search?: string
  statuses?: string[]
  sortField?: string
  sortDirection?: 'ASC' | 'DESC'
}

export interface CardFeedSavedViewModel {
  id: string
  viewType: 'CARD_FEED'
  displayName: string
  filters: CardFeedSavedViewFilters
}

interface GetCardFeedSavedViewsPayload {
  userSavedViews: CardFeedSavedViewModel[]
  pagination: {
    numberOfPages: number
    page: number
    pageItemCount: number
  }
}

export const useFetchCardFeedSavedViews = () => {
  const requestConfig = useRequestConfig()
  return useInfiniteQuery({
    queryKey: cardFeedSavedViewsQueryKeys.list(),
    queryFn: async ({ pageParam = 0 }) => {
      const params = { count: 50, page: pageParam }
      return await axios
        .get(
          `/usersavedviews/CARD_FEED?${queryString.stringify(params)}`,
          requestConfig,
        )
        .then((response) => response.data as GetCardFeedSavedViewsPayload)
    },
    getNextPageParam: (lastPage: GetCardFeedSavedViewsPayload) => {
      if (lastPage.pagination.page < lastPage.pagination.numberOfPages - 1) {
        return lastPage.pagination.page + 1
      }
      return undefined
    },
    select: (
      response: InfiniteData<GetCardFeedSavedViewsPayload, number>,
    ): GetCardFeedSavedViewsPayload => ({
      userSavedViews: response?.pages.flatMap((page) => page.userSavedViews),
      pagination: {
        page: response?.pages[0]?.pagination.page,
        numberOfPages: response?.pages[0]?.pagination.numberOfPages,
        pageItemCount: response?.pages[0]?.pagination.pageItemCount,
      },
    }),
    initialPageParam: 0,
  })
}

interface CreateCardFeedSavedViewVariables {
  displayName: string
  filters: CardFeedSavedViewFilters
}

const extractCreatedCardFeedSavedView = (
  data: unknown,
): CardFeedSavedViewModel => {
  const candidate =
    (data as any)?.userSavedView ?? (data as any)?.savedView ?? data
  if (!candidate || typeof (candidate as any).id !== 'string') {
    throw new Error('Invalid saved view create response')
  }
  return candidate as CardFeedSavedViewModel
}

export const useCreateCardFeedSavedView = () => {
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()
  return useMutation<
    CardFeedSavedViewModel,
    AxiosError<BackendErrorPayload<string>>,
    CreateCardFeedSavedViewVariables
  >({
    mutationFn: async ({
      displayName,
      filters,
    }: CreateCardFeedSavedViewVariables) => {
      return await axios
        .post(
          '/usersavedviews',
          { viewType: 'CARD_FEED', displayName, filters },
          requestConfig,
        )
        .then((response) => extractCreatedCardFeedSavedView(response.data))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cardFeedSavedViewsQueryKeys.list(),
      })
    },
  })
}

export const useDeleteCardFeedSavedView = () => {
  const queryClient = useQueryClient()
  return useExtendDelete<{}, { body: { savedViewId: string } }>({
    mutationUrl: (vars) => `/usersavedviews/${vars.body.savedViewId}`,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cardFeedSavedViewsQueryKeys.list(),
      })
    },
  })
}

// Card feed view ↔ saved filter mapping helpers.
// These use CardFeedCardsViewShape (structurally compatible with CardFeedCardsView
// from src/app) to avoid a queries→app circular dependency.

const VALID_CARD_FEED_STATUSES = new Set<string>([
  'ASSIGNED',
  'UNASSIGNED',
  'PENDING_INVITE_SENT',
  'PENDING',
  'FAILED',
])

// The card-feed columns that support sorting. Mirrors SORTABLE_FIELDS in the
// table; kept as a local set (like VALID_CARD_FEED_STATUSES) to avoid a
// queries→app dependency.
const VALID_CARD_FEED_SORT_FIELDS = new Set<string>([
  'cardName',
  'cardHolder',
  'status',
  'dateAdded',
])

export interface CardFeedCardsViewShape {
  search?: string
  statuses?: CardFeedCardStatusFilter[]
  sortField?: string
  sortDirection?: 'ASC' | 'DESC'
}

export const cardFeedViewToFilters = (
  view: CardFeedCardsViewShape,
): CardFeedSavedViewFilters => ({
  // Trim so incidental whitespace isn't persisted as a "non-empty" search —
  // the table search logic trims, so " " behaves identically to an empty
  // search and would otherwise cause false "unsaved" change detection.
  search: view.search?.trim() || undefined,
  statuses:
    view.statuses && view.statuses.length > 0
      ? view.statuses.map(String)
      : undefined,
  sortField: view.sortField,
  sortDirection: view.sortDirection,
})

export const filtersToCardFeedView = (
  filters: CardFeedSavedViewFilters,
): CardFeedCardsViewShape => ({
  // Normalize so applying a saved view never populates the search box with
  // leading/trailing whitespace the table search logic would ignore.
  search: filters.search?.trim() || undefined,
  statuses: filters.statuses?.filter((s) => VALID_CARD_FEED_STATUSES.has(s)) as
    | CardFeedCardStatusFilter[]
    | undefined,
  // Drop an unrecognized sortField (legacy/renamed column or unexpected backend
  // data) the same way invalid statuses are filtered out. Otherwise the apply
  // path falls back to the default sort while the change-detection baseline
  // keeps the raw field, flagging the view as "unsaved" with no user edit.
  sortField:
    filters.sortField && VALID_CARD_FEED_SORT_FIELDS.has(filters.sortField)
      ? filters.sortField
      : undefined,
  sortDirection: filters.sortDirection,
})
